import { useState, useRef, useEffect, useCallback, useMemo } from "react";

// Hooks
import useViewport from "./hooks/useViewport";
import { useTeams } from "./hooks/useTeams";
import { useAgenda } from "./hooks/useAgenda";
import { useProyectos } from "./hooks/useProyectos";
import { useCalendarios } from "./hooks/useCalendarios";
import { useVacaciones } from "./hooks/useVacaciones";
import { useFichaje } from "./hooks/useFichaje";
import { useCronometro } from "./hooks/useCronometro";
import { useTicketsNuevos } from "./hooks/useTicketsNuevos";
import { useGuardias } from "./hooks/useGuardias";
import { useIntegraciones } from "./hooks/useIntegraciones";
import useFocoModal from "./hooks/useFocoModal";
import * as api from "./api";

// Constants y CSS
import { CSS, AG_TIPOS_SEED } from "./constants";
import { buildFestivos, puedeGestionarEquipos, esAdmin, esGestor, esSuper, puedeVerGuardias, puedeVerIncidencias, puedeVerMisTareas, puedeProponerA, puedeVerUsuarios, puedeEditarUsuario, usuariosVisiblesPor, rolesConcedibles,
         avisoJornadaPendiente, reproducirAviso } from "./utils";

// Componentes base
import Icon from "./components/Icon";
import { BtnPrimary } from "./components/Atoms";

// Formularios

// Modales
import GlobalSearch from "./components/modals/GlobalSearch";
import PdfPreviewOverlay from "./components/modals/PdfPreviewOverlay";
import HomePdfPreviewOverlay from "./components/modals/HomePdfPreviewOverlay";

// Páginas
import HomeDashboard from "./pages/HomeDashboard";
import FichajeHeaderButton from "./components/widgets/FichajeHeaderButton";
import ProximaReunionHeaderButton from "./components/widgets/ProximaReunionHeaderButton";
import CronometroWidget from "./components/widgets/CronometroWidget";
import CronometroHeaderButton from "./components/widgets/CronometroHeaderButton";
import HelpdeskPage from "./pages/HelpdeskPage";
import MisTareasPage from "./components/MisTareasPage";
import { recordarVuelta, tomarVuelta } from "./volverDeIntegracion";
import AvisosTickets from "./components/widgets/AvisosTickets";
import CampanaNotificaciones from "./components/widgets/CampanaNotificaciones";
import ProfilePage from "./pages/ProfilePage";
import TeamsPage from "./pages/TeamsPage";
import UsersPage from "./pages/UsersPage";
import CuadernoPage from "./pages/CuadernoPage";
import Toast from "./components/widgets/Toast";
import ModalesSinMarco from "./components/layout/ModalesSinMarco";
import ModalesGenerales from "./components/layout/ModalesGenerales";
import ModalesProyecto from "./components/layout/ModalesProyecto";
import SeccionCalendario from "./components/layout/SeccionCalendario";
import SeccionProyectos from "./components/layout/SeccionProyectos";
import Sidebar from "./components/layout/Sidebar";
import { PAGE_META } from "./components/layout/pageMeta";

// Modales con datos a medias: un clic fuera no los cierra, porque se pierde lo
// escrito. Salen con Escape o con su boton Cancelar.
// Modales que traen su propio marco y su propio foco: la caja de App queda
// vacia y no debe anunciarse como dialogo.
const MODALES_SIN_MARCO = new Set(["importarGuardias", "importarVacaciones", "calendarios", "cierreJornada", "importCuaderno"]);
const MODALES_FORMULARIO = new Set([
  "nuevoProyecto", "newTeam", "editarProyecto", "nuevoPaso", "editarPaso",
  "guardarProtocolo", "usarProtocolo", "editTeam", "modifyMembers", "eventoCal", "vacacion",
]);

export default function ALM({ currentUser: initUser, token, onLogout }) {
  const [users,         setUsers       ] = useState([]);
  const [currentUser, setCurrentUser] = useState(initUser || null);
  const [page,          setPage        ] = useState("home");
  const [cuadernoViewUser,setCuadernoViewUser] = useState(null);   // admin viendo el cuaderno de otro
  const [sidebarExpanded, setSidebarExpanded] = useState(true);
  const [sidebarPeek, setSidebarPeek] = useState(false);
  // El layout del armazon (sidebar, cabecera) se decide en JS, no en CSS, asi
  // que necesita re-renderizar al redimensionar o rotar: antes se leia
  // window.innerWidth en cada render pero nada disparaba ese render, y al girar
  // el movil el sidebar se quedaba con la geometria de la orientacion anterior.
  const { isMobile, isTablet, hover } = useViewport();
  const sidebarOpen = isMobile ? sidebarExpanded : (sidebarExpanded || sidebarPeek);

  // En tablet el sidebar arranca como rail de iconos: 224px de menu fijo sobre
  // 768-1024 dejan el contenido sin ancho util. Al volver a escritorio se
  // reabre solo. Se compara contra el valor previo para no pisar al usuario
  // cada vez que decide abrirlo o cerrarlo a mano.
  const compactoPrev = useRef(null);
  useEffect(() => {
    const compacto = isMobile || isTablet;
    if (compactoPrev.current === null) { compactoPrev.current = compacto; if (compacto) setSidebarExpanded(false); return; }
    if (compactoPrev.current !== compacto) { compactoPrev.current = compacto; setSidebarExpanded(!compacto); setSidebarPeek(false); }
  }, [isMobile, isTablet]);
  const [modal,         setModal       ] = useState(null);
  const modalRef = useRef(null);
  const tipoModal = typeof modal === "string" ? modal : modal?.type;
  const modalConMarco = !!modal && !MODALES_SIN_MARCO.has(tipoModal);
  useFocoModal(modalRef, modalConMarco, () => setModal(null), tipoModal);
  const [toast,         setToast       ] = useState(null);
  const [agendaPrefill, setAgendaPrefill] = useState(null);
  const [notifs,        setNotifs      ] = useState([]);
  // Seccion Proyectos: sub-pestaña, proyecto abierto y filtros de la vista
  // general. Los cinco estados projF* que vivian aqui sueltos desde el
  // ProjectsPage anterior son estos mismos filtros, ahora en un solo objeto que
  // se pasa tal cual a getProyectos.
  const [projTab,   setProjTab  ] = useState("general");
  const [selNotionInicial, setSelNotionInicial] = useState(false);   // «Traer tareas de Notion» entra con Seleccionar activo
  const [projAbierto, setProjAbierto] = useState(null);   // id del proyecto abierto
  // Los filtros de partida los decide la propia pagina (el usuario puede
  // configurarlos), asi que hasta que los anuncia no hay nada que pedir.
  const [projFiltros, setProjFiltros] = useState(null);
  // Pasos enlazables desde el cuaderno. Se cargan una vez por sesion: son la
  // lista de "¿en que paso has estado trabajando?", no datos de pantalla.
  const [misPasos, setMisPasos] = useState([]);
  const [searchOpen,    setSearchOpen  ] = useState(false);
  const [searchQ,       setSearchQ     ] = useState("");
  const [pdfPreview,    setPdfPreview  ] = useState(null);
  const [homePdf,       setHomePdf     ] = useState(null);
  const [agendaTipos,     setAgendaTipos    ] = useState([]);
  const [agendaTiposData, setAgendaTiposData] = useState([]);

  // ── Callbacks estables — deben ir ANTES de los hooks personalizados ─────────
  // Un fallo que se repite no debe apilar diez avisos iguales: el guardado del
  // lienzo dispara una peticion por arrastre, asi que con el servidor caido
  // salian siete "Error al guardar" seguidos, cada uno con su propio temporizador
  // pisando al anterior. Se agrupa el mensaje identico y se dice CUANTAS veces
  // ha pasado, que es el dato que importa y el que se perdia.
  const toastTimer = useRef(null);
  const ultimoToast = useRef({ msg:null, t:0, veces:0 });
  const showToast = (msg, type="ok") => {
    const ahora = Date.now();
    const repetido = ultimoToast.current.msg === msg && ahora - ultimoToast.current.t < 8000;
    const veces = repetido ? ultimoToast.current.veces + 1 : 1;
    ultimoToast.current = { msg, t: ahora, veces };
    setToast({ msg, type, veces });
    clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(()=>setToast(null), type==="error"?8000:3000);
  };
  // Notion y Microsoft: lo sincronizado y las acciones que lo refrescan
  const { notionItems, setNotionItems, outlookEvents, setOutlookEvents, integrations, setIntegrations,
          reloadIntegrations, syncNotion, upsertNotionItem, removeNotionItem, handleIntegrationSync } = useIntegraciones(showToast);

  // setShowNotifs se quedo aqui cuando el panel de notificaciones se mudo a
  // CampanaNotificaciones (aa291d9): la referencia ya no existia y cada clic en
  // el menu lanzaba un ReferenceError que tumbaba el arbol de React, asi que la
  // navegacion no funcionaba en ninguna resolucion.
  const go = (pg, pid=null) => {
    if(isMobile) setSidebarExpanded(false);
    setPage(pg);
    setSearchOpen(false);
  };

  const loadNotifs = useCallback(() => {
    api.getNotifications().then(data => {
      if(Array.isArray(data)) setNotifs(data);
    }).catch(()=>{});
  }, []); // eslint-disable-line

  const loadAgendaTipos = useCallback(async (userId) => {
    if(!token) return;
    try {
      let data = await api.getAgendaTipos(userId);
      if(Array.isArray(data)) {
        // Si el usuario no tiene tipos propios, sembrar los defaults con color e icono
        if(data.length === 0 && !userId) {
          const created = [];
          for(const seed of AG_TIPOS_SEED) {
            try {
              const row = await api.createAgendaTipo(seed);
              if(row?.id) created.push(row);
            } catch(e) {}
          }
          data = created;
        }
        setAgendaTiposData(data);
        setAgendaTipos(data.map(t => t.name || t));
      }
    } catch(e) {}
  }, [token]);

  // ── Hooks API ─────────────────────────────────────────────────────────────
  const { teams, setTeams, teamsCargados, loadTeams, createTeam, updateTeam, deleteTeam, addTeamMember, removeTeamMember } = useTeams(token, showToast);

  // ── Permisos de la sesión ─────────────────────────────────────────────────
  // Incidencias y guardias son del equipo TIC, así que dependen de los equipos
  // y no solo del rol. Mientras la lista no ha llegado no se decide nada: el
  // menú aparece un instante después, pero nadie sale expulsado de su página.
  const verIncidencias = useMemo(() => puedeVerIncidencias(currentUser, teams), [currentUser, teams]);
  const verMisTareas = useMemo(() => puedeVerMisTareas(currentUser, teams), [currentUser, teams]);
  const verGuardias    = useMemo(() => puedeVerGuardias(currentUser, teams),    [currentUser, teams]);
  const verUsuarios    = puedeVerUsuarios(currentUser);
  const adminYo        = esAdmin(currentUser);

  const { agenda, setAgenda, loadAgenda, createEntry, updateEntry, deleteEntry } = useAgenda(token, currentUser, showToast, teams);
  const {
    calendarios, eventosCal, loadCalendarios, loadEventosCal,
    createCalendario, updateCalendario, deleteCalendario,
    createEvento, updateEvento, deleteEvento,
    importarCalendario, duplicarAnio,
  } = useCalendarios(token, showToast);

  const { vacaciones, loadVacaciones, sincronizarYCargar, createVacacion, updateVacacion,
          reemplazarVacacion, deleteVacacion, importarVacaciones } = useVacaciones(token, showToast);
  const proyectos = useProyectos(token, showToast);
  const fichaje = useFichaje(token, showToast);
  // Si el servidor rechaza un arranque por no estar fichado, lo mas probable es
  // que la salida se haya fichado en Odoo sin que ALM se haya enterado.
  const crono = useCronometro(token, showToast, fichaje.cargarEstado);

  // «Cambiar de tarea», se pulse donde se pulse, acaba en el MISMO sitio: el
  // buscador de tareas del panel de inicio. Repetirlo en la cabecera y en la
  // ficha de incidencia serian tres copias del mismo selector. La señal es un
  // contador porque hay que poder pedirlo dos veces seguidas.
  const [senalElegir, setSenalElegir] = useState(0);
  const irAElegirTarea = () => { go("home"); setSenalElegir(n => n + 1); };

  // Fichar salida con tareas en marcha abre el cierre del dia: el tiempo tiene
  // que aterrizar en el cuaderno hoy, o manana ya nadie se acuerda de cuanto
  // fue. Se envuelve `fichar` en vez de tocar el widget y la cabecera, asi los
  // DOS botones de salida pasan por aqui sin cambiar una linea de sus
  // componentes.
  const ficharConCierre = async () => {
    const salia = fichaje.estado === "dentro";
    const habia = crono.hayAbiertos;
    // El cronometro se congela ANTES de la salida: al reves, el tramo abierto
    // sigue contando con la persona ya fuera. Si la salida falla, quedan en
    // pausa y se reanudan con un clic, que es preferible a contar de mas.
    if (salia && habia) await crono.pausarTodo();
    const r = await fichaje.fichar();
    if (r && salia && habia) setModal("cierreJornada");
    // Volver a fichar entrada es un descanso, no un olvido: se apaga el aviso.
    if (r && !salia) crono.setPendienteCierre(false);
    return r;
  };
  const fichajeUI = { ...fichaje, fichar: ficharConCierre };

  // El aviso de las nueve horas vive AQUI y no en el indicador de la cabecera.
  //
  // Estaba alli porque era el unico con un reloj corriendo, pero ese indicador
  // ya no se pinta en Inicio —el widget grande dice lo mismo—, y con el escondido
  // el aviso no saltaba: justo a quien se deja el panel abierto toda la tarde.
  // Un aviso de fin de jornada no puede depender de en que pagina estes.
  //
  // Cada 30 s basta: `avisoJornadaPendiente` ya se encarga de que salte una sola
  // vez al dia por persona, asi que la precision no cambia nada.
  useEffect(() => {
    if (fichaje.estado !== "dentro") return;
    const mirar = () => {
      const cerradas = (fichaje.hoy || [])
        .filter(t => t.check_out)
        .reduce((s, t) => s + (t.horas || 0), 0) * 3600000;
      const enCurso = fichaje.desde ? Date.now() - new Date(fichaje.desde).getTime() : 0;
      if (avisoJornadaPendiente(currentUser?.id, cerradas + enCurso)) {
        showToast("Llevas mas de 9 horas. Ya es hora de ir a casa, buen trabajo.", "warn");
        reproducirAviso();
      }
    };
    mirar();
    const id = setInterval(mirar, 30000);
    return () => clearInterval(id);
  }, [fichaje.estado, fichaje.desde, fichaje.hoy, currentUser?.id]); // eslint-disable-line
  // Los avisos de incidencia nueva solo tienen sentido con Odoo conectado
  const avisosTickets = useTicketsNuevos(token, fichaje.conectado === true && verIncidencias, currentUser?.id);
  const [ticketInicial,setTicketInicial]=useState(null);
  const [hiloInicial,setHiloInicial]=useState(null);   // id de un encargo: abre su hilo en Mis tareas

  const { guardias, loadGuardias, setGuardia, aplicarPlan: aplicarPlanGuardias, limpiar: limpiarGuardias, importar: importarGuardias } = useGuardias(token, showToast);

  // Festivos vigentes: los comparten el calendario, las guardias, las vacaciones
  // y el aviso del cuaderno
  const festivos = useMemo(() => buildFestivos(eventosCal), [eventosCal]);

  // Sub-pestaña dentro de Calendario y año que muestran las vistas anuales
  const [calTab,  setCalTab ] = useState("mes");
  const [fichajeDia, setFichajeDia] = useState(null);         // dia al que lleva un aviso de fichaje corregido
  const [fichTab, setFichTab] = useState("vacaciones");   // pestana de la entrada Asistencias
  const [calYear, setCalYear] = useState(() => new Date().getFullYear());

  // ── Cargar datos desde API ─────────────────────────────────────────────────
  useEffect(() => {
    if(token) {
      loadTeams();
      loadAgenda();
      loadAgendaTipos();
      loadCalendarios();
      loadEventosCal();
      loadNotifs();
      // Load integrations and their data
      api.getIntegrations().then(data => { if(Array.isArray(data)) setIntegrations(data); }).catch(()=>{});
      api.getNotionItems()
        .then(data => { if(Array.isArray(data)) setNotionItems(data); })
        .catch(e => showToast(`No se pudieron cargar los elementos de Notion: ${e.message}`, "error"));
      api.getOutlookEvents().then(data => { if(Array.isArray(data)) setOutlookEvents(data); }).catch(()=>{});
      // Check if returning from OAuth flow
      const params = new URLSearchParams(window.location.search);
      const integration = params.get("integration");
      // Se vuelve de conectar Microsoft desde otra pagina (Mis tareas): ALM se recarga de cero, asi que se
      // devuelve a la persona a donde estaba. El estado de To Do se lee de nuevo al abrir el menu.
      const volver = tomarVuelta(integration, ["microsoft", "notion"]);
      if (volver) setPage(volver);
      if (integration?.endsWith("_ok")) {
        showToast(volver === "tareas" ? (integration === "notion_ok" ? "Notion conectado: ya puedes traer tus tareas" : "Microsoft conectado: ya puedes poner recordatorios en To Do") : "Integración conectada correctamente");
        window.history.replaceState({}, "", window.location.pathname);
      }
      if (integration?.endsWith("_error")) {
        showToast("Error al conectar la integración", "error");
        window.history.replaceState({}, "", window.location.pathname);
      }
      const interval = setInterval(loadNotifs, 30000);
      // El aviso de la cabecera cuenta los minutos que faltan, asi que la lista
      // tiene que seguir siendo verdad segun avanza el dia: una reunion movida
      // a media manana no puede quedarse pintada a su hora vieja. El servidor
      // repuebla su cache al leerlo, asi que con pedirlo basta.
      const relojCalendario = setInterval(() => {
        api.getOutlookEvents().then(d => { if(Array.isArray(d)) setOutlookEvents(d); }).catch(()=>{});
      }, 10 * 60 * 1000);
      return () => { clearInterval(interval); clearInterval(relojCalendario); };
    }
  }, [token]);

  // La lista de proyectos se recarga cuando cambian los filtros de la vista
  // general. El backend resuelve el ambito (quien ve que), asi que aqui no hay
  // que decidir nada: a un member le llegan solo los suyos.
  useEffect(() => {
    if (token && projFiltros) proyectos.loadProyectos(projFiltros);
  }, [token, projFiltros]); // eslint-disable-line

  // Si el gestor de proyectos todavia no esta migrado en el servidor esto
  // responde 500; el cuaderno se queda sin el selector de paso y sigue
  // funcionando igual, asi que el error no se saca por pantalla.
  //
  // Se piden CON desbloqueo: es una sola lista para los dos consumidores. El
  // cronometro necesita saber si un paso se puede empezar ya; el cuaderno ignora
  // ese dato (alli se imputan horas a posteriori, tambien a pasos cerrados).
  // Mantener dos listas casi iguales en App costaba una peticion mas y una
  // fuente de verdad mas que se podia quedar atras.
  const recargarPasos = useCallback(() => {
    if (!token) return;
    api.getMisPasos(true)
      .then(d => { if (Array.isArray(d)) setMisPasos(d); })
      .catch(e => console.warn("[proyectos] no se pudieron leer los pasos:", e.message));
  }, [token]);

  useEffect(() => { recargarPasos(); }, [recargarPasos]);

  // Guardias y vacaciones se consultan por año: al cambiarlo hay que recargar,
  // o las vistas anuales se quedarían con la ventana por defecto del backend.
  useEffect(() => {
    if (!token) return;
    // Al resto de la plantilla el backend le devuelve 403: no se le pide.
    if (verGuardias) loadGuardias(`${calYear}-01-01`, `${calYear}-12-31`);
    // Antes de listar se refresca contra Odoo, que es quien manda en las
    // ausencias: el importador nocturno solo llega a quien tiene su cuenta
    // conectada, y con la sesión de quien mira se ve la plantilla entera.
    sincronizarYCargar(`${calYear}-01-01`, `${calYear}-12-31`);
  }, [token, calYear, verGuardias, loadGuardias, sincronizarYCargar]);

  // Si alguien está en una página o pestaña que ya no le corresponde (le han
  // cambiado el rol o lo han sacado del equipo), se le devuelve a algo suyo.
  useEffect(() => {
    if (!token || !teamsCargados) return;
    if (page === "helpdesk" && !verIncidencias) go("home");
    if (page === "users"    && !verUsuarios)    go("home");
    if (calTab === "guardias" && !verGuardias)  setCalTab("mes");
    if (fichTab === "actividad" && !esGestor(currentUser)) setFichTab("vacaciones");
  }, [token, teamsCargados, page, calTab, fichTab, verIncidencias, verUsuarios, verGuardias]); // eslint-disable-line

  // ── Cargar usuarios ────────────────────────────────────────────────────────
  useEffect(() => {
    if(!token) return;
    const h = { "Content-Type":"application/json", Authorization:`Bearer ${token}` };
    const apiUrl = process.env.REACT_APP_API_URL || "http://localhost:3001/api";
    // La lista completa es de admin y gestor; el resto recibe 403, pero sí
    // necesita los nombres para el destinatario del cuaderno → directorio ligero.
    // El gestor (lote S6) recibe de /users solo a su gente: el resto de nombres
    // le llega, como a todos, del directorio, y se mezclan.
    const pedir = url => fetch(url, {headers:h}).then(r=>r.json()).then(d=>Array.isArray(d) ? d : null);
    const mezclar = (fichas, dir) => {
      if (!dir) return fichas;
      const por = new Map((fichas||[]).map(u=>[String(u.id),u]));
      const enDir = new Set(dir.map(u=>String(u.id)));
      return [...dir.map(u=>por.get(String(u.id))||u), ...(fichas||[]).filter(u=>!enDir.has(String(u.id)))];
    };
    (verUsuarios ? pedir(`${apiUrl}/users`) : Promise.resolve(null))
      .then(async fichas => (verUsuarios && adminYo) ? fichas : mezclar(fichas, await pedir(`${apiUrl}/users/directory`)))
      .then(d=>{ if(Array.isArray(d)) setUsers(d); })
      .catch(()=>{});
  }, [token, verUsuarios, adminYo]);

  // ── Ctrl+K búsqueda global ─────────────────────────────────────────────────
  useEffect(()=>{
    const h=(e)=>{ if((e.ctrlKey||e.metaKey)&&e.key==="k"){ e.preventDefault(); if(modal) return; setSearchOpen(p=>!p); setSearchQ(""); }};
    window.addEventListener("keydown",h);
    return ()=>window.removeEventListener("keydown",h);
  },[modal]);

  // ── Comprobar deadlines de tareas y persistirlos en BD ────────────────────
  useEffect(()=>{
    if(!token) return;
    api.checkDeadlines().then(()=>loadNotifs()).catch(()=>{});
  },[token]); // eslint-disable-line

  const inS = {width:"100%",padding:"8px 11px",borderRadius:8,border:"1px solid var(--border)",fontSize:13,fontFamily:"var(--font)",boxSizing:"border-box",background:"var(--surface2)",color:"var(--text)",marginBottom:11};
  const lbS = {fontSize:10,color:"var(--text3)",fontWeight:700,display:"block",marginBottom:4,textTransform:"uppercase",letterSpacing:".06em"};
  const card = {background:"var(--surface)",borderRadius:13,border:"1px solid var(--border)",boxShadow:"var(--shadow-sm)"};


  return (
    <>
      <style>{CSS}</style>
      <div className="app-shell" style={{display:"flex",background:"var(--bg)",fontFamily:"var(--font)",fontSize:13,color:"var(--text)",overflow:"hidden"}}>
        {/* ── SIDEBAR ── */}
        {/* Overlay móvil — clic fuera cierra sidebar */}
        <Sidebar sidebarExpanded={sidebarExpanded} setSidebarExpanded={setSidebarExpanded} setSidebarPeek={setSidebarPeek}
          sidebarOpen={sidebarOpen} isMobile={isMobile} hover={hover} page={page} go={go} setModal={setModal}
          verIncidencias={verIncidencias} verMisTareas={verMisTareas} verUsuarios={verUsuarios} currentUser={currentUser}
          integrations={integrations} onLogout={onLogout}/>

        {/* ── MAIN ── */}
        {/* El menu ya NO se pliega al pulsar en el contenido: se abre y se cierra con su
            boton. Plegarse solo al primer clic dejaba a la gente sin las etiquetas justo
            cuando acababa de entrar y no sabia donde ir. */}
        <div style={{flex:1,display:"flex",flexDirection:"column",overflow:"hidden"}}>
          <header style={{background:"var(--surface)",borderBottom:"1px solid var(--border)",flexShrink:0,boxShadow:"var(--shadow-sm)",position:"relative"}}>
            {/* ── Fila principal: icono/título + búsqueda + notificaciones ── */}
            <div className="hdr-main" style={{padding:"8px 20px",display:"flex",alignItems:"center",gap:12}}>
              {isMobile&&(
                <button aria-label="Abrir o cerrar el menú" aria-expanded={sidebarExpanded} className="btn" onClick={()=>setSidebarExpanded(p=>!p)}
                  style={{flexShrink:0,padding:"6px 8px",borderRadius:8,border:"1px solid var(--border)",background:"var(--surface2)",cursor:"pointer",display:"flex",alignItems:"center"}}>
                  <svg aria-hidden="true" xmlns="http://www.w3.org/2000/svg" width="18" height="18" fill="var(--text2)" viewBox="0 0 16 16">
                    <path fillRule="evenodd" d="M2.5 12a.5.5 0 0 1 .5-.5h10a.5.5 0 0 1 0 1H3a.5.5 0 0 1-.5-.5zm0-4a.5.5 0 0 1 .5-.5h10a.5.5 0 0 1 0 1H3a.5.5 0 0 1-.5-.5zm0-4a.5.5 0 0 1 .5-.5h10a.5.5 0 0 1 0 1H3a.5.5 0 0 1-.5-.5z"/>
                  </svg>
                </button>
              )}
              {(()=>{
                const meta = PAGE_META[page] || {icon:"house-fill", color:"#0b7d96", label:"Inicio"};
                const viewing = page==="cuaderno" ? cuadernoViewUser : null;
                return (
                  <>
                    <div style={{position:"absolute",left:0,top:0,bottom:0,width:3.5,
                      background:`linear-gradient(to bottom,${meta.color},${meta.color}00)`}}/>
                    <div style={{display:"flex",alignItems:"center",gap:11,flex:1,minWidth:0,paddingLeft:10}}>
                      <Icon name={meta.icon} size={21} color={meta.color}
                        style={{flexShrink:0,filter:`drop-shadow(0 2px 7px ${meta.color}bb)`}}/>
                      <div style={{minWidth:0}}>
                        <div style={{fontWeight:800,fontSize:15.5,letterSpacing:"-.025em",color:"var(--text)",lineHeight:1.1,
                          overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",display:"flex",alignItems:"center",gap:8}}>
                          <span style={{overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{meta.label}</span>
                          {viewing&&(
                            <span style={{display:"inline-flex",alignItems:"center",gap:5,padding:"2px 9px",borderRadius:20,
                              fontSize:11,fontWeight:700,letterSpacing:0,flexShrink:0,
                              background:`${meta.color}1f`,color:meta.color,border:`1px solid ${meta.color}55`}}>
                              <Icon name="eye" size={11} color={meta.color}/>{viewing.name}
                            </span>
                          )}
                        </div>
                        {(viewing?`Viendo el cuaderno de ${viewing.name}`:meta.sub)&&<div className="hdr-sub" style={{fontSize:10.5,color:viewing?meta.color:"var(--text3)",marginTop:1.5,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{viewing?`Viendo el cuaderno de ${viewing.name}`:meta.sub}</div>}
                      </div>
                    </div>
                  </>
                );
              })()}
              {/* Búsqueda global + Notificaciones */}
              <div style={{display:"flex",alignItems:"center",gap:6,marginLeft:"auto"}}>
                {/* Los dos indicadores solo FUERA de Inicio: alli estan los
                    widgets grandes con lo mismo, y dos contadores de la misma
                    cosa en la misma pantalla es informacion duplicada que
                    ademas puede desincronizarse un segundo y hacer dudar de
                    las dos. */}
                {page!=="home"&&(
                  <>
                    <CronometroHeaderButton crono={crono} onIrAInicio={()=>go("home")}
                      dentro={fichaje.estado==="dentro"}
                      onCambiarDeTarea={irAElegirTarea}/>
                    <FichajeHeaderButton fichaje={fichajeUI}/>
                  </>
                )}
                {/* La proxima reunion se pinta en TODAS las paginas, Inicio
                    incluido: a diferencia del cronometro y del fichaje, no hay
                    ningun widget alla que diga lo mismo. Y se pinta solo si
                    queda algo hoy: el propio boton se esconde cuando no. */}
                <ProximaReunionHeaderButton eventos={outlookEvents}
                  onVerCalendario={()=>{setCalTab("mes");go("calendario");}}/>
                <button className="btn" onClick={()=>{setSearchOpen(p=>!p);setSearchQ("");}}
                  title="Búsqueda global (Ctrl+K)" aria-label="Búsqueda global"
                  style={{padding:"6px 12px",borderRadius:8,border:"1px solid var(--border)",background:searchOpen?"var(--accent-bg)":"var(--surface)",color:searchOpen?"var(--accent)":"var(--text2)",cursor:"pointer",display:"flex",alignItems:"center",gap:6,fontSize:11.5,fontFamily:"var(--font)"}}>
                  <Icon name="search" size={13} color={searchOpen?"var(--accent)":"var(--text2)"}/>
                  <span className="m-hide" style={{fontWeight:500}}>Buscar</span>
                  <kbd className="m-hide" style={{fontSize:10,padding:"1px 5px",borderRadius:4,background:"var(--bg)",border:"1px solid var(--border)",color:"var(--text3)",fontFamily:"monospace",lineHeight:1.6}}>Ctrl K</kbd>
                </button>
                <CampanaNotificaciones
                  notifs={notifs} setNotifs={setNotifs}
                  avisosTickets={avisosTickets}
                  onAbrirTicket={id=>{setTicketInicial(id);go("helpdesk");}}
                  onAbrirFichaje={iso=>{setFichajeDia(iso);setFichTab("asistencias");go("fichajes");}}
                  onAbrirEncargo={id=>{setHiloInicial(id);go("tareas");}}/>
              </div>
            </div>
            {/* ── Subcabecera 1: acciones principales ── */}
            {page==="teams"&&puedeGestionarEquipos(currentUser,teams)&&(
              <div style={{padding:"5px 20px 6px",display:"flex",alignItems:"center",gap:7,borderTop:"1px solid var(--border2)",background:"var(--surface2)",flexWrap:"wrap"}}>
                <BtnPrimary onClick={()=>setModal("newTeam")}><Icon name="plus-lg" size={13} color="#fff"/>Nuevo equipo</BtnPrimary>
              </div>
            )}
          </header>

          <div style={{flex:1,display:"flex",overflow:"hidden"}}>
            {/* ── CUADERNO PAGE ── ocupa todo el main sin padding propio */}
            {page==="cuaderno"&&(
              <div style={{flex:1,display:"flex",flexDirection:"column",overflow:"hidden"}}>
                <CuadernoPage
                  misPasos={misPasos} canVerIncidencias={verIncidencias} crono={crono}
                  onAbrirIncidencia={verIncidencias?(id=>{setTicketInicial(id);go("helpdesk");}):undefined}
                  onCreateEntry={createEntry}
                  onUpdateEntry={updateEntry}
                  onDeleteEntry={deleteEntry}
                  agenda={agenda} setAgenda={setAgenda}
                  notionItems={notionItems}
                  currentUser={currentUser} allUsers={users} teams={teams}
                  showToast={showToast} festivos={festivos}
                  prefill={agendaPrefill} clearPrefill={()=>setAgendaPrefill(null)}
                  agendaTipos={agendaTipos} setAgendaTipos={setAgendaTipos}
                  agendaTiposData={agendaTiposData} setAgendaTiposData={setAgendaTiposData}
                  loadAgendaTipos={loadAgendaTipos}
                  loadAgenda={loadAgenda}
                  onExportPdf={(entries,view)=>setModal({type:"exportCuaderno", agenda: entries, view})}
                  onImport={()=>setModal({type:"importCuaderno"})}
                  onViewingUserChange={setCuadernoViewUser}/>
              </div>
            )}

            {/* ── CALENDARIO PAGE ── */}
            {page==="calendario"&&(
              <SeccionCalendario calTab={calTab} setCalTab={setCalTab} calYear={calYear} setCalYear={setCalYear} verGuardias={verGuardias}
              currentUser={currentUser} outlookEvents={outlookEvents} agenda={agenda} calendarios={calendarios} eventosCal={eventosCal}
              vacaciones={vacaciones} users={users} teams={teams} festivos={festivos} guardias={guardias} showToast={showToast}
              setModal={setModal} setGuardia={setGuardia} aplicarPlanGuardias={aplicarPlanGuardias} limpiarGuardias={limpiarGuardias}
              diaInicial={fichajeDia} alUsarDiaInicial={()=>setFichajeDia(null)}/>
            )}

            {/* ── PROYECTOS ── gestor nativo + Notion como una opcion mas.
                Va fuera de <main>, como Cuaderno y Calendario, porque el lienzo
                del diagrama de flujo necesita la altura completa. */}
            {page==="fichajes"&&(
              <SeccionCalendario seccion="fichajes" fichaje={fichajeUI} calTab={fichTab} setCalTab={setFichTab} calYear={calYear} setCalYear={setCalYear} verGuardias={verGuardias}
              currentUser={currentUser} outlookEvents={outlookEvents} agenda={agenda} calendarios={calendarios} eventosCal={eventosCal}
              vacaciones={vacaciones} users={users} teams={teams} festivos={festivos} guardias={guardias} showToast={showToast}
              setModal={setModal} setGuardia={setGuardia} aplicarPlanGuardias={aplicarPlanGuardias} limpiarGuardias={limpiarGuardias}/>
            )}

            {page==="projects"&&(
              <SeccionProyectos projTab={projTab} setProjTab={setProjTab} projAbierto={projAbierto} setProjAbierto={setProjAbierto}
              setProjFiltros={setProjFiltros} proyectos={proyectos} users={users} teams={teams} currentUser={currentUser}
              festivos={festivos} setModal={setModal} crono={crono} agendaTiposData={agendaTiposData} misPasos={misPasos}
              showToast={showToast} notionItems={notionItems} integrations={integrations} token={token} syncNotion={syncNotion}
              upsertNotionItem={upsertNotionItem} removeNotionItem={removeNotionItem} reloadIntegrations={reloadIntegrations}
              seleccionNotionInicial={selNotionInicial} onSeleccionNotionVista={()=>setSelNotionInicial(false)}/>
            )}

            {page!=="cuaderno"&&page!=="calendario"&&page!=="fichajes"&&page!=="projects"&&(
              <main className="main-content" style={{flex:1,overflowY:"auto",padding:20}}>

                {/* ── DASHBOARD INICIO ── */}
                {page==="home"&&(
                  <HomeDashboard
                    notionItems={notionItems} agenda={agenda}
                    agendaTiposData={agendaTiposData}
                    go={go} setModal={setModal}
                    currentUser={currentUser}
                    fichaje={fichajeUI}
                    onHomePdf={setHomePdf}
                    crono={crono} misPasos={misPasos} verIncidencias={verIncidencias}
                    senalElegir={senalElegir}
                    onAbrirPanel={()=>go("helpdesk")}
                    onCerrarJornada={()=>setModal("cierreJornada")}
                    verMisTareas={verMisTareas}
                    onAbrirProyecto={p=>{setProjTab("general");setProjAbierto(p.id);proyectos.loadProyecto(p.id);go("projects");}}/>
                )}

                {/* ── MIS TAREAS — salida gradual: solo admin y TIC de momento ── */}
                {page==="tareas"&&verMisTareas&&(
                  <MisTareasPage showToast={showToast} miId={currentUser?.id} puedeAprobar={esGestor(currentUser)}
                    propuestasA={users.filter(u=>u.active!==false&&puedeProponerA(currentUser,u,teams)).map(u=>{const equipos=teams.filter(t=>(t.members||[]).map(String).includes(String(u.id))).map(t=>t.name);return{id:String(u.id),nombre:String(u.id)===String(currentUser?.id)?"Yo mismo":u.name,equipo:equipos[0]||null,equipos};})}
                    equiposMios={teams.filter(t=>(t.members||[]).map(String).includes(String(currentUser?.id))).map(t=>t.name)} hiloInicial={hiloInicial} onHiloInicialVisto={()=>setHiloInicial(null)}
                    onTraerNotion={()=>{setSelNotionInicial(true);setProjTab("notion");setProjAbierto(null);proyectos.setDetalle(null);go("projects");}}
                    onConectarMicrosoft={async()=>{
                      // El mismo flujo que Integraciones (IntegrationsModal): el servidor pide ya el permiso de To Do
                      recordarVuelta("tareas");
                      try { window.location.href = await api.integrationConnectUrl("microsoft"); }
                      catch(e){ showToast(e.message||"No se pudo iniciar la conexión con Microsoft","error"); }
                    }}
                    notionConectado={integrations.some(i=>i.provider==="notion")}
                    onConectarNotion={async()=>{
                      // El mismo flujo que Integraciones: al volver, de nuevo en Tareas
                      recordarVuelta("tareas");
                      try { window.location.href = await api.integrationConnectUrl("notion"); }
                      catch(e){ showToast(e.message||"No se pudo iniciar la conexión con Notion","error"); }
                    }}
                    onAbrirProyecto={p=>{setProjTab("general");setProjAbierto(p.id);proyectos.loadProyecto(p.id);go("projects");}}/>
                )}

                {/* ── INCIDENCIAS — Helpdesk de Odoo ── */}
                {page==="helpdesk"&&verIncidencias&&(
                  <HelpdeskPage
                    token={token} showToast={showToast}
                    ticketInicial={ticketInicial} onTicketInicialVisto={()=>setTicketInicial(null)}
                    senalNuevos={avisosTickets.senal}
                    crono={crono} agendaTiposData={agendaTiposData}
                    onIrAInicio={irAElegirTarea}
                    onConnect={()=>go("home")}/>
                )}


                {page==="users"&&verUsuarios&&(<UsersPage users={usuariosVisiblesPor(currentUser, users, teams)} setUsers={setUsers} teams={teams}
                  puedeEditar={esAdmin(currentUser)} puedeEditarA={u=>puedeEditarUsuario(currentUser, u, teams)} rolesConcedibles={rolesConcedibles(currentUser)} puedeNombrarSuper={esSuper(currentUser, teams)} showToast={showToast}/>)}

                {page==="profile"&&(
                  <ProfilePage user={currentUser} users={users} setUsers={setUsers} notifs={notifs} setNotifs={setNotifs} teams={teams}/>
                )}

                {/* TEAMS */}
                {page==="teams"&&(
                  <TeamsPage
                    teams={teams} users={users} currentUser={currentUser} card={card}
                    setModal={setModal} removeTeamMember={removeTeamMember} onDeleteTeam={deleteTeam}/>
                )}

              </main>
            )}

          </div>
          {/* FOOTER */}
          <footer style={{background:"#0B2849",color:"#8fb8c8",fontSize:10,padding:"6px 20px",display:"flex",alignItems:"center",justifyContent:"space-between",flexShrink:0,borderTop:"1px solid #0f2530"}}>
            <span>© {new Date().getFullYear()} Ángel Linares Montalbán · Todos los derechos reservados</span>
            <span>Desarrollado por el Dpto. TIC de Ángel Linares Montalbán</span>
          </footer>
        </div>{/* end main column */}

        {/* GLOBAL SEARCH */}
        {searchOpen&&(
          <GlobalSearch
            notionItems={notionItems} teams={teams} agenda={agenda}
            q={searchQ} setQ={setSearchQ}
            onClose={()=>{setSearchOpen(false);setSearchQ("");}}
            onAbrirCuaderno={()=>go("cuaderno")}/>
        )}

        {/* AVISOS DE INCIDENCIA NUEVA */}
        {/* Tanto abrir la tarjeta como cerrarla la dan por vista: si no, seguia
            contando como pendiente en la campana despues de haberla leido. */}
        <AvisosTickets avisos={avisosTickets.avisos}
          onCerrar={id=>{avisosTickets.marcarLeido(id);avisosTickets.descartar(id);}}
          onAbrir={a=>{avisosTickets.marcarLeido(a.id);avisosTickets.descartar(a.id);
                       setTicketInicial(a.id);go("helpdesk");}}/>

        {/* TOAST */}
        <Toast toast={toast} setToast={setToast}/>

        {/* MODALES */}
        {modal&&(
          <div className="modal-bg modal-pad" style={{position:"fixed",inset:0,background:"rgba(0,0,0,.38)",backdropFilter:"blur(3px)",zIndex:100,display:"flex",alignItems:"center",justifyContent:"center",padding:20}} onClick={()=>{ if(!MODALES_FORMULARIO.has(tipoModal)) setModal(null); }}>
            <div ref={modalRef} role={modalConMarco?"dialog":undefined} aria-modal={modalConMarco?"true":undefined} className="modal-box" style={{background:"var(--surface)",borderRadius:16,boxShadow:"var(--shadow-lg)",width:"100%",
              // Los formularios con dos selectores de fecha en paralelo se quedan
              // estrechos a 460 y el calendario desplegado no cabe a lo alto
              maxWidth: (modal?.type==="vacacion"||modal?.type==="eventoCal"
                          ||modal==="nuevoProyecto"||modal?.type==="editarProyecto"
                          ||modal?.type==="guardarProtocolo"||modal?.type==="usarProtocolo"
                          ||modal?.type==="borrarProyecto"
                          ||modal?.type==="nuevoPaso"||modal?.type==="editarPaso") ? 560 : 460,
              padding:24,maxHeight:"92vh",overflowY:"auto"}} onClick={e=>e.stopPropagation()}>
              <ModalesProyecto modal={modal} setModal={setModal} inS={inS} lbS={lbS} teams={teams} users={users}
                currentUser={currentUser} proyectos={proyectos} setProjAbierto={setProjAbierto} setProjTab={setProjTab}/>
              <ModalesGenerales modal={modal} setModal={setModal} inS={inS} lbS={lbS}
                createTeam={createTeam} updateTeam={updateTeam} users={users} teams={teams} currentUser={currentUser}
                agendaTipos={agendaTipos} agendaTiposData={agendaTiposData} notionItems={notionItems} agenda={agenda}
                setPdfPreview={setPdfPreview} calendarios={calendarios} createEvento={createEvento} updateEvento={updateEvento}
                deleteEvento={deleteEvento} festivos={festivos} createVacacion={createVacacion} updateVacacion={updateVacacion}
                reemplazarVacacion={reemplazarVacacion} deleteVacacion={deleteVacacion} showToast={showToast}
                integrations={integrations} token={token} reloadIntegrations={reloadIntegrations}
                handleIntegrationSync={handleIntegrationSync}/>
            </div>
          </div>
        )}
      </div>{/* end outer row */}

      {/* PDF PREVIEW OVERLAY — fuera del modal, full screen */}
      {pdfPreview&&(
        <PdfPreviewOverlay {...pdfPreview} onClose={()=>setPdfPreview(null)}/>
      )}
      {homePdf&&(
        <HomePdfPreviewOverlay {...homePdf} onClose={()=>setHomePdf(null)}/>
      )}
      <ModalesSinMarco modal={modal} setModal={setModal} users={users} teams={teams} currentUser={currentUser}
        calendarios={calendarios} createCalendario={createCalendario} updateCalendario={updateCalendario}
        deleteCalendario={deleteCalendario} importarCalendario={importarCalendario} duplicarAnio={duplicarAnio}
        importarGuardias={importarGuardias} importarVacaciones={importarVacaciones} agendaTiposData={agendaTiposData}
        crono={crono} loadAgenda={loadAgenda} recargarPasos={recargarPasos} setAgenda={setAgenda} showToast={showToast}/>
    </>
  );
}
